import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadCloud, X, Loader2, CheckCircle2, AlertCircle, Music } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ENERGIES, readAudioDuration, uploadTrackChunked } from '../../utils/catalogUpload';

const newKey = () => Math.random().toString(36).slice(2, 10);

export const CatalogUploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [batch, setBatch] = useState({ album: '', genre: '', energy: '', language: '', year: '', label: '' });
  const [uploading, setUploading] = useState(false);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const staged = files.map((file) => ({
      key: newKey(),
      file,
      title: file.name.replace(/\.(mp3|m4a|aac|wav|flac|ogg)$/i, ''),
      artist: '',
      duration_ms: 0,
      status: 'staged',
      progress: 0,
      error: '',
    }));
    setRows((prev) => [...prev, ...staged]);
    staged.forEach(async (row) => {
      const d = await readAudioDuration(row.file);
      setRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, duration_ms: d } : r)));
    });
    if (fileRef.current) fileRef.current.value = '';
  };

  const patch = (key, p) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const remove = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

  const startUpload = async () => {
    if (!rows.length) return;
    for (const r of rows) {
      if (!r.title.trim()) {
        toast.error(`"${r.file.name}" needs a title`);
        return;
      }
    }
    setUploading(true);
    let ok = 0;
    for (const row of rows) {
      if (row.status === 'done') continue;
      patch(row.key, { status: 'uploading', progress: 0, error: '' });
      try {
        const meta = {
          title: row.title.trim(),
          artist: row.artist.trim(),
          album: batch.album.trim(),
          genre: batch.genre.trim(),
          energy: batch.energy,
          language: batch.language.trim(),
          year: Number(batch.year) || 0,
          label: batch.label.trim(),
          duration_ms: row.duration_ms || 0,
        };
        // eslint-disable-next-line no-await-in-loop, no-loop-func
        await uploadTrackChunked(row.file, meta, (p) => patch(row.key, { progress: p }));
        patch(row.key, { status: 'done', progress: 100 });
        ok += 1;
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Upload failed';
        patch(row.key, { status: 'error', error: typeof detail === 'string' ? detail : 'Upload failed' });
      }
    }
    setUploading(false);
    if (ok) {
      toast.success(`${ok} track${ok === 1 ? '' : 's'} added to the catalog`);
      onUploaded?.();
      setRows((prev) => prev.filter((r) => r.status !== 'done'));
      if (!rows.some((r) => r.status === 'error')) onOpenChange(false);
    }
  };

  const close = () => {
    if (uploading) return;
    setRows([]);
    setBatch({ album: '', genre: '', energy: '', language: '', year: '', label: '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-2xl bg-[#0a0a0a] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="catalog-upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Upload songs</DialogTitle>
        </DialogHeader>

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          className="border-2 border-dashed border-white/15 rounded-2xl p-8 text-center cursor-pointer hover:border-[#FF007F]/60 hover:bg-white/[0.02] transition-colors"
          data-testid="catalog-dropzone"
        >
          <UploadCloud className="w-8 h-8 mx-auto text-[#FF007F] mb-2" />
          <p className="text-sm text-gray-300">Drop audio files here or <span className="text-[#FF007F] font-semibold">browse</span></p>
          <p className="text-[11px] text-gray-500 mt-1">MP3, M4A, AAC, WAV, FLAC, OGG · up to 80 MB each</p>
          <input ref={fileRef} type="file" accept=".mp3,.m4a,.aac,.wav,.flac,.ogg,audio/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="catalog-file-input" />
        </div>

        {rows.length > 0 && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px] text-gray-400">Album</Label>
                <Input value={batch.album} onChange={(e) => setBatch((b) => ({ ...b, album: e.target.value }))} className="h-9 bg-black/50 border-white/10" data-testid="batch-album" />
              </div>
              <div>
                <Label className="text-[11px] text-gray-400">Genre</Label>
                <Input value={batch.genre} onChange={(e) => setBatch((b) => ({ ...b, genre: e.target.value }))} placeholder="pop, rock…" className="h-9 bg-black/50 border-white/10" data-testid="batch-genre" />
              </div>
              <div>
                <Label className="text-[11px] text-gray-400">Energy</Label>
                <Select value={batch.energy || undefined} onValueChange={(v) => setBatch((b) => ({ ...b, energy: v }))}>
                  <SelectTrigger className="h-9 bg-black/50 border-white/10" data-testid="batch-energy"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{ENERGIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] text-gray-400">Language</Label>
                <Input value={batch.language} onChange={(e) => setBatch((b) => ({ ...b, language: e.target.value }))} className="h-9 bg-black/50 border-white/10" data-testid="batch-language" />
              </div>
              <div>
                <Label className="text-[11px] text-gray-400">Year</Label>
                <Input value={batch.year} onChange={(e) => setBatch((b) => ({ ...b, year: e.target.value.replace(/\D/g, '').slice(0, 4) }))} className="h-9 bg-black/50 border-white/10" data-testid="batch-year" />
              </div>
              <div>
                <Label className="text-[11px] text-gray-400">Label</Label>
                <Input value={batch.label} onChange={(e) => setBatch((b) => ({ ...b, label: e.target.value }))} className="h-9 bg-black/50 border-white/10" data-testid="batch-label" />
              </div>
            </div>
            <p className="text-[11px] text-gray-500 -mt-1">These apply to every file in this batch. Fine-tune any song after upload.</p>

            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {rows.map((r) => (
                <div key={r.key} className="flex items-center gap-2 bg-white/[0.03] rounded-xl p-2" data-testid={`staged-row-${r.key}`}>
                  <Music className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                    <Input value={r.title} onChange={(e) => patch(r.key, { title: e.target.value })} placeholder="Title" disabled={uploading} className="h-8 bg-black/50 border-white/10 text-sm" />
                    <Input value={r.artist} onChange={(e) => patch(r.key, { artist: e.target.value })} placeholder="Artist" disabled={uploading} className="h-8 bg-black/50 border-white/10 text-sm" />
                  </div>
                  <div className="w-24 flex-shrink-0">
                    {r.status === 'uploading' && <Progress value={r.progress} className="h-1.5" />}
                    {r.status === 'done' && <CheckCircle2 className="w-4 h-4 text-[#39FF14] ml-auto" />}
                    {r.status === 'error' && <span className="text-[10px] text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{r.error}</span>}
                  </div>
                  {r.status !== 'uploading' && (
                    <button type="button" onClick={() => remove(r.key)} className="text-gray-500 hover:text-white flex-shrink-0" data-testid={`remove-staged-${r.key}`}><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={uploading} data-testid="catalog-upload-cancel">Cancel</Button>
          <Button onClick={startUpload} disabled={uploading || !rows.length} className="bg-[#FF007F] hover:bg-[#CC0066]" data-testid="catalog-upload-start">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : `Upload ${rows.length || ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
